import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import type {
  ResearchCustomerInteractionsToolInput,
  ResearchCustomerInteractionsToolOutput,
} from "@repo/app-types/agents/researchCustomerInteractionsTool";
import {
  assertUnreachable,
  renderPlural,
} from "@repo/app-types/helpers/utilities";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";

export const ResearchCustomerInteractionsTool: ToolComponent<
  ResearchCustomerInteractionsToolInput,
  ResearchCustomerInteractionsToolOutput
> = (props) => {
  const renderText = () => {
    if (props.status.type !== "complete" || !props.output) {
      return "Reviewing customer interactions...";
    }

    if (checkIsErrorToolOutput(props.output)) {
      return "An error occurred while reviewing customer interactions";
    }

    const summary = props.output.coverage_limit_exceeded
      ? renderCoverageLimitSummary(props.output.coverage_limit_exceeded)
      : renderReviewedSummary(props.input, props.output);
    // This tool has no expandable details view, so name the playbook inline
    // (only present when the agent is scoped to several playbooks).
    return props.output.playbook
      ? `${summary} · ${props.output.playbook.name}`
      : summary;
  };

  return <ChatToolBase status={props.status}>{renderText()}</ChatToolBase>;
};

function renderCoverageLimitSummary(
  coverageLimit: NonNullable<
    ResearchCustomerInteractionsToolOutput["coverage_limit_exceeded"]
  >,
): string {
  switch (coverageLimit.reason) {
    case "population_over_limit":
      return `Too many customer interactions to review every one (${coverageLimit.population.toLocaleString()}, limit ${coverageLimit.limit.toLocaleString()})`;
    case "transcript_search_capped":
      return `Too many transcript matches to review every one (over ${coverageLimit.limit.toLocaleString()})`;
    default:
      return assertUnreachable(coverageLimit.reason);
  }
}

function renderReviewedSummary(
  input: ResearchCustomerInteractionsToolInput,
  output: ResearchCustomerInteractionsToolOutput,
): string {
  // Threads from before 2026-10 stored the analyzed count as `counts.sample`;
  const analyzed = output.counts.analyzed ?? output.counts.sample;
  const reviewed = renderPlural(analyzed, {
    zero: "No customer interactions reviewed",
    one: "Reviewed 1 customer interaction",
    // TODO: input contains raw LLM tool call args (before Zod transforms),
    // so selection can be null even though the schema transforms it to a default.
    // Revisit after upgrading Mastra — newer versions may apply transforms before
    // passing args to the UI component.
    other: (count) => {
      const mode = input.selection?.mode ?? "most_recent";
      switch (mode) {
        case "most_recent":
          return `Reviewed ${count.toLocaleString()} recent customer interactions`;
        case "random":
          return `Reviewed a sample of ${count.toLocaleString()} customer interactions`;
        case "all":
          return `Reviewed ${count.toLocaleString()} of ${output.counts.population.toLocaleString()} customer interactions`;
        default:
          return assertUnreachable(mode);
      }
    },
  });
  return input.included_categories
    ? `${reviewed}, ${output.findings.length.toLocaleString()} matched`
    : reviewed;
}
